import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService, NbMenuItem, NbMenuService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';

import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { CourseStructureDialogComponent } from '../dialogs/course-structure-dialog/course-structure-dialog.component';
import { ParsedCourseStructureDialogComponent } from '../../pages/moodle-dashboard/detail-dialogs/course-structure-dialog.component';
import {
  LtiPlacementDialogComponent,
  LtiPlacementDialogCourse,
} from '../dialogs/lti-placement-dialog/lti-placement-dialog.component';
import {
  IliasObject,
  IliasSoapConfig,
  IliasSoapInput,
  LmsPlacement,
  LmsRegistration,
  LmsRegistryService,
} from '../../services/lms-registry.service';
import { ChatPreviewService } from '../../services/chat-preview.service';
import { DataSource } from '../../services/data-sources.service';
import {
  DOZENT_COMPATIBLE_TUTOR_TEMPLATES,
  DozentBot,
  Tutor,
  TutorService,
  TutorTemplate,
} from '../../services/tutor.service';

/**
 * Eine ILIAS-Verbindung mit ihren Platzierungen und ihrem SOAP-Zugang.
 *
 * Unterschied zur generischen LTI-Karte: ILIAS hat kein Plugin von uns, aber
 * einen SOAP-Server. Der ist dort das Äquivalent — ohne ihn funktioniert der
 * Launch zwar, es fehlen aber Kursliste, Mitglieder und Lernfortschritt. Die
 * Karte macht diesen zweiten Kanal deshalb sichtbar und prüfbar, statt ihn in
 * einem Untermenü zu verstecken.
 *
 * Der Kurs-Sync schreibt in dieselben kanonischen Tabellen wie Moodle, weil die
 * an der Registrierung hängen und nicht an einer Moodle-Eigenschaft. Alles
 * danach — Kurs-Picker, Placement-Auflösung, Wissensbasis-Scoping — gilt damit
 * für ILIAS unverändert.
 */
/** Eine Zeile der Tabelle: ein ILIAS-Kurs, dazu die Platzierung, falls es
 *  schon eine gibt. Kursgetrieben wie bei Moodle — die Platzierung ist eine
 *  Spalte im Kurs, nicht umgekehrt. */
/** Die bearbeitbaren Felder einer Zeile. Wortgleich mit dem Puffer der
 *  Moodle-Karte, damit beide Karten dieselbe Bedienung haben: tippen,
 *  auswählen, dann Speichern. */
interface IliasRowEdit {
  botId: string | null;
  kbIds: string[];
  dozentEnabled: boolean;
  dozentBotId: string | null;
  courseSyncEnabled: boolean;
  structureParseEnabled: boolean;
}

export interface IliasCourseRow {
  course: IliasObject;
  placement: LmsPlacement | null;
  /** Zuordnungen, die an einer einzelnen LTI-Aktivitaet dieses Kurses
   *  haengen — entstanden, weil eine Lehrkraft im Kurs ein „Externes
   *  Tool" angelegt und dort einen Agenten gewaehlt hat.
   *
   *  Sie standen in dieser Karte nirgends, und das war irrefuehrend in
   *  beide Richtungen: ein Kurs mit Agent im LTI-Objekt sah unbesetzt aus,
   *  und nach einer Plugin-Kopplung erschien auf seinen Seiten nichts,
   *  ohne dass irgendwo stand warum. Das Plugin liest ausschliesslich
   *  kursweite Zeilen. */
  activityPlacements: LmsPlacement[];
  /** Bearbeitungspuffer der Zellen. Gefüllt aus der Platzierung und dem
   *  Agenten der Zeile; nach erfolgreichem Speichern wird er zum neuen
   *  Ausgangsstand, damit die Zeile wieder sauber ist. */
  edit: IliasRowEdit;
  initial: IliasRowEdit;
  /** Läuft gerade ein Speichern-Aufruf dieser Zeile? */
  saving: boolean;
  /** Eigene Marke für den Inhalts-Schalter: er greift sofort, nicht über
   *  Speichern — deshalb nicht dieselbe wie `saving`. */
  savingSync: boolean;
  /** Dasselbe für den Didaktik-Schalter. */
  savingStructureParse: boolean;
}

type IliasPlacementFilter = 'any' | 'placed' | 'unplaced';
type IliasSyncFilter = 'any' | 'enabled' | 'disabled';

@Component({
  selector: 'app-ilias-placements-card',
  templateUrl: './ilias-placements-card.component.html',
  styleUrl: './ilias-placements-card.component.scss',
})
export class IliasPlacementsCardComponent implements OnInit, OnDestroy {
  @Input() registration!: LmsRegistration;
  @Input() tutors: Tutor[] = [];
  @Input() courses: LtiPlacementDialogCourse[] = [];
  /** Wissensbasen des Mandanten. Sie hängen am Agenten, nicht an der
   *  Platzierung — die Spalte schreibt deshalb auf den Tutor, genau wie die
   *  Moodle-Karte. */
  @Input() dataSources: DataSource[] = [];
  /** Verfügbare Twins. Die Verknüpfung sitzt am Agenten, nicht an der
   *  Platzierung — sie gilt deshalb für ILIAS genauso wie für Moodle. */
  @Input() dozentBots: DozentBot[] = [];
  /** Placement-Zeilen tragen die client_id als Platzhalter für „kein Kurs". */
  @Input() clientId: string | null = null;

  placements: LmsPlacement[] = [];
  /** Die abgeglichenen ILIAS-Kurse. Sie tragen die Tabelle; ohne sie bleibt
   *  nur der Hinweis, dass noch nie abgeglichen wurde. */
  iliasCourses: IliasObject[] = [];
  /** Alles Abgeglichene, auch Ordner und Dateien — Grundlage der Zählung je
   *  Kurs und der Kategorie-Auswahl. */
  allObjects: IliasObject[] = [];
  childCount = new Map<number, number>();
  /** Vorlagen je Kennung. Die Twin-Verknüpfung verlangt eine passende
   *  Agentenvorlage; ohne sie antwortet der Server mit 400, und ein Schalter,
   *  der garantiert scheitert, gehört deaktiviert. */
  private templatesById = new Map<string, TutorTemplate>();
  loading = true;
  coursesLoading = true;

  search = '';
  /** Drei Filter wie bei Moodle: Bereich, Platzierung, Inhaltsabgleich. */
  categoryFilter = '';
  placementFilter: IliasPlacementFilter = 'any';
  syncFilter: IliasSyncFilter = 'any';
  /** Auswahl für Sammelaktionen, über die ref_id — sie überlebt ein Neuladen,
   *  die Platzierungskennung nicht. */
  selected = new Set<number>();

  soap: IliasSoapConfig | null = null;
  soapOpen = false;
  soapSaving = false;
  soapTesting = false;
  syncing = false;

  /** Formularwerte. Das Passwort startet leer und bleibt leer, solange der
   *  Admin nichts tippt — dann sendet das Formular es nicht mit und der
   *  Server behält das gespeicherte. */
  form = { soap_url: '', ilias_client: '', username: '', password: '', template_ref_id: null as number | null };

  private destroy$ = new Subject<void>();

  constructor(
    private lms: LmsRegistryService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private t: TranslateService,
    private router: Router,
    private tutorService: TutorService,
    private chatPreviewService: ChatPreviewService,
    private nbMenuService: NbMenuService,
  ) {}

  /** Zur Einrichtung. Technischer Zugang und Zeitplan liegen im
   *  Verbindungsdialog unter Integrationen — hier geht es allein darum,
   *  welcher Agent wo antwortet. */
  goToIliasOverview(): void {
    this.router.navigate(['/integrations']);
  }

  /** Welche Zeile gerade angelegt wird — je Kontext, nicht global, damit die
   *  übrigen Zeilen bedienbar bleiben. */
  placingRefId: string | null = null;

  /** Ohne Vorlage kann SOAP kein konfiguriertes LTI-Objekt erzeugen; dann ist
   *  der Knopf eine Sackgasse und bleibt weg. */
  get canPlaceInIlias(): boolean {
    return !!this.soap?.configured && !!this.soap?.template_ref_id;
  }

  placeInIlias(placement: LmsPlacement): void {
    const refId = Number.parseInt(String(placement.lti_context_id || ''), 10);
    if (!Number.isFinite(refId) || this.placingRefId) return;
    this.placingRefId = placement.lti_context_id || null;

    this.lms.createIliasLtiObject(this.registration.id, refId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.placingRefId = null;
          this.toastr.success(
            this.t.instant('pages.tutors.placements.ilias.placedToast', {
              refId: result.ref_id,
            }), '',
          );
        },
        error: (err) => {
          this.placingRefId = null;
          this.toastr.danger(
            err?.error?.detail
              || this.t.instant('pages.tutors.placements.ilias.placeFailed'),
            '',
          );
        },
      });
  }

  ngOnInit(): void {
    this.listenToRowMenu();
    this.reload();
    this.loadCourses();
    this.loadSoap();

    this.tutorService.tutorTemplates$
      .pipe(takeUntil(this.destroy$))
      .subscribe((templates) => {
        this.templatesById = new Map(templates.map((t) => [t.id, t]));
      });
    if (this.tutorService.tutorTemplates$.value.length === 0) {
      this.tutorService.listTutorTemplates().pipe(takeUntil(this.destroy$)).subscribe();
    }
  }

  /** Die abgeglichenen Kurse dieser Verbindung. Fehlschläge bleiben still:
   *  die Karte zeigt dann den Leerzustand, der ohnehin erklärt, dass noch
   *  nichts abgeglichen wurde. */
  loadCourses(): void {
    this.coursesLoading = true;
    // Alles Abgeglichene, nicht nur Kurse: die Spalte „Inhalte" zählt, was in
    // einem Kurs liegt, und dafür braucht es die Kinder.
    this.lms.listIliasObjects(this.registration.id, undefined, '')
      .pipe(takeUntil(this.destroy$), catchError(() => of([] as IliasObject[])))
      .subscribe((rows) => {
        this.allObjects = rows;
        this.iliasCourses = rows.filter(
          (o) => o.object_type === 'crs' || o.object_type === 'grp',
        );
        this.childCount = new Map<number, number>();
        for (const obj of rows) {
          if (obj.parent_external_id == null) continue;
          const parent = Number(obj.parent_external_id);
          this.childCount.set(parent, (this.childCount.get(parent) || 0) + 1);
        }
        this.coursesLoading = false;
        this.buildRows();
      });
  }

  /** Wie viele abgeglichene Objekte in diesem Kurs liegen. Die Entsprechung
   *  zur Aktivitätenzahl der Moodle-Karte — nur heissen sie in ILIAS Dateien,
   *  Ordner, Tests und Lernmodule. */
  objectCount(row: IliasCourseRow): number {
    return this.childCount.get(row.course.external_id) || 0;
  }

  /** Aufschlüsselung der Objektarten eines Kurses, für den Tooltip der
   *  Zählmarke: „4 Dateien · 1 Test · 2 Ordner".
   *
   *  Möglich, seit der Baumabgleich jede nicht-administrative Art speichert
   *  statt einer festen Liste — vorher lagen nur Kurse in der Tabelle und die
   *  Zahl war immer 0. Unbekannte Arten stehen mit ihrem ILIAS-Kürzel da;
   *  eine erfundene Übersetzung wäre schlechter als das Kürzel, das auch in
   *  ILIAS selbst steht. */
  objectTypeSummary(row: IliasCourseRow): string {
    const counts = new Map<string, number>();
    for (const obj of this.allObjects) {
      if (obj.parent_external_id == null) continue;
      if (Number(obj.parent_external_id) !== row.course.external_id) continue;
      const type = obj.object_type || '';
      counts.set(type, (counts.get(type) || 0) + 1);
    }
    if (counts.size === 0) {
      return this.t.instant('pages.tutors.placements.ilias.col.objectsEmptyTooltip');
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([type, count]) => `${count} ${this.objectTypeLabel(type, count)}`)
      .join(' · ');
  }

  private objectTypeLabel(type: string, count: number): string {
    const key = `pages.tutors.placements.ilias.objectType.${type}.${count === 1 ? 'one' : 'many'}`;
    const label = this.t.instant(key);
    // `instant` gibt den Schlüssel zurück, wenn es keine Übersetzung gibt.
    return label === key ? type : label;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Platzierungen ─────────────────────────────────────────────────
  reload(): void {
    this.loading = true;
    this.lms
      .listPlacements(this.registration.id)
      .pipe(takeUntil(this.destroy$), catchError(() => of([] as LmsPlacement[])))
      .subscribe((rows) => {
        this.placements = rows
          .filter((p) => !!p.lti_context_id)
          .sort((a, b) =>
            String(a.lti_context_id).localeCompare(String(b.lti_context_id), undefined, { numeric: true }));
        this.loading = false;
        this.buildRows();
      });
  }

  trackPlacement = (_: number, p: LmsPlacement) => p.id;

  trackRow = (_: number, row: IliasCourseRow) => row.course.external_id;

  /** Läuft für diese Zeile gerade das Anlegen in ILIAS? `placingRefId` hält
   *  die Kontext-ID als Zeichenkette, die Zeile ihre ref_id als Zahl. */
  isPlacing(row: IliasCourseRow): boolean {
    return this.placingRefId === String(row.course.external_id);
  }

  /** Platzierung eines Kurses, über die Kontext-ID. ILIAS nennt beim Launch
   *  die ref_id als `context_id` — dieselbe Zahl, die in der Kurszeile steht. */
  placementFor(course: IliasObject): LmsPlacement | null {
    const ref = String(course.external_id);
    return this.placements.find(
      (p) => String(p.lti_context_id) === ref && !p.lti_resource_link_id,
    ) || null;
  }

  /** Die Aktivitaets-Zuordnungen desselben Kurses. Gegenstueck zu
   *  `placementFor`, das nur die kursweite nimmt. */
  activityPlacementsFor(course: IliasObject): LmsPlacement[] {
    const ref = String(course.external_id);
    return this.placements.filter(
      (p) => String(p.lti_context_id) === ref && !!p.lti_resource_link_id,
    );
  }

  /** Die Tabelle: jeder abgeglichene Kurs eine Zeile, Suche und Filter
   *  angewandt. Kurse ohne Platzierung stehen gleichberechtigt drin — genau
   *  dort entsteht die Zuordnung. */
  rows: IliasCourseRow[] = [];

  /** Zeilen aufbauen. Wie `buildRows` in der Moodle-Karte: je Kurs eine
   *  Zeile, der Puffer aus Platzierung und Agent gefüllt.
   *
   *  Eine Zeile mit ungespeicherten Änderungen behält ihren Puffer. Das ist
   *  nötig, weil `tutors` als Eingabe später eintreffen kann als die Kurse
   *  und jeder Abgleich neu aufbaut — sonst verschwände eine gerade
   *  getroffene Auswahl unter der Hand. */
  private buildRows(): void {
    const previous = new Map<number, IliasCourseRow>(
      this.rows.map((r) => [r.course.external_id, r]),
    );
    this.rows = this.iliasCourses.map((course) => {
      const placement = this.placementFor(course);
      const botId = this.isAgentPlacement(placement) ? (placement?.bot_id || null) : null;
      const tutor: any = botId ? this.tutors.find((t) => t.id === botId) : undefined;
      const initial: IliasRowEdit = {
        botId,
        kbIds: [...(tutor?.tutor_data_source_ids || tutor?.data_source_ids || [])],
        dozentEnabled: !!tutor?.attached_dozent_id,
        dozentBotId: tutor?.attached_dozent_id || null,
        courseSyncEnabled: !!placement?.course_sync_enabled,
        structureParseEnabled: !!(placement as any)?.structure_parse_enabled,
      };
      const old = previous.get(course.external_id);
      const keepEdit = !!old && this.isDirty(old);
      return {
        course,
        placement,
        activityPlacements: this.activityPlacementsFor(course),
        edit: keepEdit ? old!.edit : { ...initial, kbIds: [...initial.kbIds] },
        initial,
        saving: false,
        savingSync: old?.savingSync || false,
        savingStructureParse: old?.savingStructureParse || false,
      } as IliasCourseRow;
    });
  }

  /** Suche und die drei Filter. Der Inhaltsfilter liest den Puffer, damit ein
   *  gerade umgelegter Schalter die Zeile sofort richtig einordnet. */
  get filteredRows(): IliasCourseRow[] {
    const needle = this.search.trim().toLowerCase();
    return this.rows.filter((row) => {
      if (needle) {
        const haystack = `${row.course.title} ${row.course.path || ''}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (this.categoryFilter && this.categoryOf(row.course) !== this.categoryFilter) {
        return false;
      }
      if (this.placementFilter === 'placed' && !row.placement) return false;
      if (this.placementFilter === 'unplaced' && row.placement) return false;
      if (this.syncFilter === 'enabled' && !row.edit.courseSyncEnabled) return false;
      if (this.syncFilter === 'disabled' && !!row.edit.courseSyncEnabled) return false;
      return true;
    });
  }

  /** Der Bereich, in dem ein Kurs liegt: alles im Repository-Pfad ausser dem
   *  Kurs selbst. Das ist die ILIAS-Entsprechung zur Moodle-Kategorie. */
  categoryOf(course: IliasObject): string {
    const path = (course.path || '').trim();
    if (!path) return '';
    const parts = path.split('/').map((p) => p.trim()).filter(Boolean);
    return parts.length > 1 ? parts.slice(0, -1).join(' / ') : '';
  }

  /** Alle vorkommenden Bereiche, alphabetisch — speist den Bereichsfilter. */
  get categories(): string[] {
    const seen = new Set<string>();
    for (const course of this.iliasCourses) {
      const cat = this.categoryOf(course);
      if (cat) seen.add(cat);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }

  // ── Sortierung und Seitenzahl ─────────────────────────────────────
  /** Nach Titel, wie bei Moodle umschaltbar. Der Pfad entscheidet nur bei
   *  gleichem Titel — zwei Kurse gleichen Namens sollen nicht willkürlich
   *  die Plätze tauschen. */
  courseSortDirection: 'asc' | 'desc' = 'asc';
  readonly pageSizeOptions = [25, 50, 100];
  pageSize = 25;
  page = 1;

  toggleCourseSort(): void {
    this.courseSortDirection = this.courseSortDirection === 'asc' ? 'desc' : 'asc';
  }

  onPageChange(page: number): void {
    this.page = Math.max(1, page || 1);
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size || 25;
    this.page = 1;
  }

  /** Die gefilterten Zeilen, sortiert. */
  get sortedRows(): IliasCourseRow[] {
    const dir = this.courseSortDirection === 'asc' ? 1 : -1;
    return [...this.filteredRows].sort((a, b) => {
      const byTitle = (a.course.title || '').localeCompare(b.course.title || '', undefined, {
        numeric: true, sensitivity: 'base',
      });
      if (byTitle !== 0) return byTitle * dir;
      return (a.course.path || '').localeCompare(b.course.path || '') * dir;
    });
  }

  /** Nur die sichtbare Seite. Auswahl und Kopfhaken arbeiten darauf, sonst
   *  würde ein Haken oben Zeilen erfassen, die niemand sieht. */
  get pagedRows(): IliasCourseRow[] {
    const start = (this.page - 1) * this.pageSize;
    return this.sortedRows.slice(start, start + this.pageSize);
  }

  get placedCount(): number {
    return this.iliasCourses.filter((c) => !!this.placementFor(c)).length;
  }

  clearSearch(): void {
    this.search = '';
  }

  // ── Auswahl ───────────────────────────────────────────────────────
  isSelected(row: IliasCourseRow): boolean {
    return this.selected.has(row.course.external_id);
  }

  toggleSelected(row: IliasCourseRow, on: boolean): void {
    if (on) this.selected.add(row.course.external_id);
    else this.selected.delete(row.course.external_id);
  }

  get allSelected(): boolean {
    const rows = this.pagedRows;
    return rows.length > 0 && rows.every((r) => this.isSelected(r));
  }

  toggleSelectAll(on: boolean): void {
    for (const row of this.pagedRows) this.toggleSelected(row, on);
  }

  get selectedRows(): IliasCourseRow[] {
    return this.rows.filter((r) => this.isSelected(r));
  }


  // ── Zuordnung je Zeile ────────────────────────────────────────────
  /** Hat die Zeile schon eine Platzierung? Entscheidet, ob rechts
   *  „Speichern" oder „Platzieren" steht und ob die Zellen bedienbar sind —
   *  dieselbe Weiche wie `rowHasAgentPlacement` in der Moodle-Karte. */
  rowHasAgentPlacement(row: IliasCourseRow): boolean {
    return this.isAgentPlacement(row.placement);
  }

  /** Eine Platzierung, die nur den Inhaltsabgleich trägt. Sie entsteht, wenn
   *  jemand Kurs-Sync einschaltet, bevor ein Agent zugeordnet ist — die Karte
   *  behandelt sie deshalb weiter wie eine Zeile ohne Agent: kein
   *  Agenten-Feld, kein Speichern, sondern der Platzieren-Knopf. Marke und
   *  Name wörtlich aus der Moodle-Karte (`metadata.sync_only`). */
  isSyncOnlyPlacement(placement?: LmsPlacement | null): boolean {
    return !!(placement?.metadata || {})['sync_only'];
  }

  isAgentPlacement(placement?: LmsPlacement | null): boolean {
    if (!placement) return false;
    // Ein zugeordneter Agent schlaegt die Marke: bekommt eine reine
    // Sync-Platzierung spaeter ueber den Dialog einen Agenten, bleibt
    // `metadata.sync_only` stehen (der Patch kennt kein metadata). Ohne diese
    // Zeile zeigte die Karte dann weiter „Platzieren" und kein Agentenfeld,
    // obwohl der Agent haengt.
    if (placement.bot_id) return true;
    return !this.isSyncOnlyPlacement(placement);
  }

  /** Weicht der Puffer vom geladenen Stand ab? Treibt den Speichern-Knopf.
   *  Die beiden Schalter zählen nicht mit — die greifen sofort, genau wie
   *  drüben bei Moodle. */
  isDirty(row: IliasCourseRow): boolean {
    if (!row.placement) return false;
    const a = row.edit;
    const b = row.initial;
    if (a.botId !== b.botId) return true;
    if (a.dozentEnabled !== b.dozentEnabled) return true;
    if (a.dozentBotId !== b.dozentBotId) return true;
    if (a.kbIds.length !== b.kbIds.length) return true;
    const before = new Set(a.kbIds);
    return b.kbIds.some((x) => !before.has(x));
  }

  /** Speichern je Zeile. Bündelt bis zu drei unabhängige Aufrufe:
   *    - PATCH /lms/placements/{uuid}   Agent an der Platzierung
   *    - PATCH /tutor/{id}              Wissensbasen am Agenten
   *    - PUT/DELETE /tutor/{id}/dozent  Twin-Verknüpfung
   *  Sie laufen zusammen; danach wird der Puffer zum neuen Ausgangsstand,
   *  ohne die ganze Tabelle neu zu laden. */
  saveRow(row: IliasCourseRow): void {
    if (!row.placement || row.saving || !this.isDirty(row)) return;
    row.saving = true;
    const calls: Observable<any>[] = [];

    if (row.edit.botId !== row.initial.botId) {
      calls.push(this.lms.updatePlacement(row.placement.id, {
        bot_id: row.edit.botId || null,
      }));
    }

    // Die Wissensbasen hängen am Agenten, nicht an der Platzierung. Die
    // Änderung gilt deshalb überall, wo derselbe Agent steht — bei Moodle
    // ist das genauso und beabsichtigt.
    const tutorId = row.edit.botId || row.initial.botId;
    if (tutorId) {
      const before = new Set(row.initial.kbIds);
      const changed = row.edit.kbIds.length !== row.initial.kbIds.length
        || row.edit.kbIds.some((x) => !before.has(x));
      if (changed) {
        calls.push(this.tutorService.updateTutor({
          id: tutorId,
          tutor_data_source_ids: row.edit.kbIds,
        } as Partial<Tutor>));
      }
    }

    // Twin anhängen, wechseln oder lösen.
    if (
      tutorId
      && (row.edit.dozentEnabled !== row.initial.dozentEnabled
        || row.edit.dozentBotId !== row.initial.dozentBotId)
    ) {
      if (row.edit.dozentEnabled && row.edit.dozentBotId) {
        calls.push(this.tutorService.attachDozent(tutorId, row.edit.dozentBotId));
      } else if (!row.edit.dozentEnabled) {
        calls.push(this.tutorService.detachDozent(tutorId));
      }
    }

    if (calls.length === 0) {
      row.saving = false;
      return;
    }

    forkJoin(calls).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        row.saving = false;
        // Die lokale Agentenkopie mitziehen, sonst zeigt eine zweite Zeile
        // mit demselben Agenten bis zum nächsten Laden den alten Stand.
        const tutor: any = this.tutors.find((t) => t.id === tutorId);
        if (tutor) {
          tutor.tutor_data_source_ids = [...row.edit.kbIds];
          tutor.attached_dozent_id = row.edit.dozentEnabled ? row.edit.dozentBotId : null;
        }
        if (row.placement) (row.placement as any).bot_id = row.edit.botId || null;
        row.initial = {
          botId: row.edit.botId,
          kbIds: [...row.edit.kbIds],
          dozentEnabled: row.edit.dozentEnabled,
          dozentBotId: row.edit.dozentBotId,
          courseSyncEnabled: row.initial.courseSyncEnabled,
          structureParseEnabled: row.initial.structureParseEnabled,
        };
        this.menuCache.delete(row.course.external_id);
        this.toastr.success(this.t.instant('pages.tutors.placements.lms.savedToast'), '');
      },
      error: (err) => {
        row.saving = false;
        this.toastr.danger(
          err?.error?.detail || this.t.instant('pages.tutors.placements.ilias.saveFailed'), '',
        );
      },
    });
  }

  /** Kursinhalte in die Wissensbasis. Greift sofort, nicht über Speichern —
   *  der Aufruf holt die Dateien gleich mit und bringt die Bilanz zurück.
   *  Der Puffer wird vorher umgelegt und bei Fehlschlag zurückgedreht, damit
   *  der Schalter nicht auf die Antwort wartet. */
  onCourseSyncToggle(row: IliasCourseRow, on: boolean): void {
    if (row.savingSync) return;
    if (row.edit.courseSyncEnabled === on) return;
    // Ohne Platzierung gibt es nichts abzuschalten.
    if (!row.placement && !on) return;
    const previous = row.edit.courseSyncEnabled;
    row.edit.courseSyncEnabled = on;
    row.savingSync = true;
    const call: Observable<any> = this.ensurePlacement(row).pipe(
      switchMap((placement) => {
        row.placement = placement;
        return on
          ? this.lms.enableIliasCourseSync(placement.id)
          : this.lms.disableIliasCourseSync(placement.id);
      }),
    );
    call.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        row.savingSync = false;
        row.initial.courseSyncEnabled = on;
        if (row.placement) (row.placement as any).course_sync_enabled = on;
        this.menuCache.delete(row.course.external_id);
        if (on) {
          const stats = res?.stats || {};
          this.toastr.success(
            this.t.instant('pages.tutors.placements.ilias.contentDone', {
              files: stats.files ?? 0,
              stored: stats.stored ?? 0,
              skipped: stats.skipped ?? 0,
            }), '',
          );
        }
        this.reload();
      },
      error: (err) => {
        row.savingSync = false;
        row.edit.courseSyncEnabled = previous;
        this.toastr.danger(
          err?.error?.detail || this.t.instant('pages.tutors.placements.ilias.contentFailed'), '',
        );
      },
    });
  }

  /** Inhalte erneut holen. Der Einschalt-Endpunkt ist absichtlich
   *  wiederholbar, damit genau das geht — der Schalter steht dabei schon auf
   *  „an" und soll nicht umspringen. */
  refreshContent(row: IliasCourseRow): void {
    if (!row.placement || row.savingSync) return;
    row.savingSync = true;
    this.lms.enableIliasCourseSync(row.placement.id)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          row.savingSync = false;
          const stats = res?.stats || {};
          this.toastr.success(
            this.t.instant('pages.tutors.placements.ilias.contentDone', {
              files: stats.files ?? 0,
              stored: stats.stored ?? 0,
              skipped: stats.skipped ?? 0,
            }), '',
          );
          this.reload();
        },
        error: (err) => {
          row.savingSync = false;
          this.toastr.danger(
            err?.error?.detail || this.t.instant('pages.tutors.placements.ilias.contentFailed'), '',
          );
        },
      });
  }

  /** Die Platzierung der Zeile, notfalls frisch angelegt.
   *
   *  Der Inhaltsabgleich hängt technisch an einer Platzierung (dort steht die
   *  ref_id und das Flag), fachlich aber am Kurs: wer Unterlagen ins Haus
   *  holen will, hat oft noch gar nicht entschieden, welcher Agent damit
   *  arbeitet. Die entstehende Zeile trägt deshalb `sync_only` und keinen
   *  Agenten — die Karte zeigt weiter den Platzieren-Knopf. Denselben Weg
   *  geht `ensurePlacement` in der Moodle-Karte. */
  private ensurePlacement(row: IliasCourseRow): Observable<LmsPlacement> {
    if (row.placement) return of(row.placement);
    return this.lms.createPlacement({
      registration_id: this.registration.id,
      lti_context_id: String(row.course.external_id),
      bot_id: null,
      metadata: { sync_only: true },
    } as any);
  }

  /** Die didaktische Analyse ist nur sinnvoll, solange Inhalte geholt werden
   *  — ohne Dateien gibt es nichts zu gliedern. */
  structureParseAvailable(row: IliasCourseRow): boolean {
    return !!row.placement && row.edit.courseSyncEnabled;
  }

  /** Kann diese Zeile den Inhaltsabgleich überhaupt einschalten? Ja, auch
   *  ohne Agent — der AlphaTutor-Kurs entsteht dabei von selbst. Nur eine
   *  pausierte Verbindung hält ihn auf. */
  courseSyncAvailable(): boolean {
    return !this.registration.disabled_at;
  }

  onStructureParseToggle(row: IliasCourseRow, on: boolean): void {
    if (!row.placement || row.savingStructureParse) return;
    if (row.edit.structureParseEnabled === on) return;
    const previous = row.edit.structureParseEnabled;
    row.edit.structureParseEnabled = on;
    row.savingStructureParse = true;
    this.lms.enableIliasCourseSync(row.placement.id, { structure_parse: on })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          row.savingStructureParse = false;
          row.initial.structureParseEnabled = on;
          if (row.placement) (row.placement as any).structure_parse_enabled = on;
          // Der Schalter loest beim Einschalten ein Parsing aus, das im
          // Hintergrund laeuft — ohne Rueckmeldung sah er aus, als passiere
          // nichts. Beim Ausschalten gibt es nichts zu melden.
          if (on) {
            this.toastr.success(
              this.t.instant(
                res?.structure_parse_triggered
                  ? 'pages.tutors.placements.ilias.structureStarted'
                  : 'pages.tutors.placements.ilias.structureEnabled',
              ), '',
            );
          }
          this.reload();
        },
        error: (err) => {
          row.savingStructureParse = false;
          row.edit.structureParseEnabled = previous;
          this.toastr.danger(
            err?.error?.detail || this.t.instant('pages.tutors.placements.ilias.saveFailed'), '',
          );
        },
      });
  }

  // ── Twin ──────────────────────────────────────────────────────────
  /** Twins lassen sich nur an Agenten hängen, deren Vorlage das kann
   *  (`alpha_tutor`, `alpha_flexible`). Bei allen anderen bleibt der Schalter
   *  aus, statt eine Ablehnung des Servers zu provozieren. */
  isDozentCompatible(botId: string | null | undefined): boolean {
    if (!botId) return false;
    const tutor = this.tutors.find((t) => t.id === botId);
    if (!tutor?.template_id) return false;
    const template = this.templatesById.get(tutor.template_id);
    return !!template && DOZENT_COMPATIBLE_TUTOR_TEMPLATES.has(template.name);
  }

  dozentName(botId: string | null | undefined): string {
    if (!botId) return '';
    return this.dozentBots.find((d) => d.id === botId)?.name || botId;
  }

  // ── Zustand je Zeile ──────────────────────────────────────────────
  /** Vier Zustände, in dieser Reihenfolge geprüft: ohne Agent gibt es nichts
   *  zu melden, danach entscheidet der Inhaltsabgleich. */
  /** Fünf Zustände, wie bei Moodle, in dieser Reihenfolge geprüft.
   *
   *  `global` ist der, den ich zuerst vergessen hatte: deckt der
   *  verbindungsweite Umfang den Kurs ab, laufen die Inhalte auch ohne den
   *  Schalter in der Zeile. Ohne diesen Zustand hätte dort „Aus" gestanden,
   *  während der Abgleich längst lief. */
  rowStatus(row: IliasCourseRow): 'unplaced' | 'disabled' | 'syncing' | 'active' | 'global' {
    if (row.savingSync || row.savingStructureParse || row.saving) return 'syncing';
    if (!row.placement) return 'unplaced';
    if (this.connectionScopeCovers(row)) return 'global';
    return row.edit.courseSyncEnabled ? 'active' : 'disabled';
  }

  /** Deckt der verbindungsweite Inhalts-Umfang diese Zeile ab? „Alle Kurse"
   *  trifft jeden, „nur platzierte" jeden mit Platzierung. */
  connectionScopeCovers(row: IliasCourseRow): boolean {
    const reg: any = this.registration;
    if (!reg?.auto_course_sync_enabled) return false;
    return (reg.auto_course_sync_mode || 'placements') === 'all' || !!row.placement;
  }

  /** Die Klasse der Zustandsmarke. Absichtlich Moodles Vokabular
   *  (`is-active`, `is-disabled`, `is-syncing`, `is-global`, `is-muted`),
   *  damit die Farben aus dem geteilten Stylesheet greifen. */
  rowStatusClass(row: IliasCourseRow): string {
    const state = this.rowStatus(row);
    return state === 'unplaced' ? 'is-muted' : `is-${state}`;
  }

  rowStatusKey(row: IliasCourseRow): string {
    return `pages.tutors.placements.ilias.status.${this.rowStatus(row)}`;
  }

  // ── Vorschau ──────────────────────────────────────────────────────
  /** Welche Zeile gerade vorschaut. Über die ref_id, weil die Platzierung
   *  beim Neuladen eine neue Kennung bekommen kann. */
  activePreviewRefId: number | null = null;

  isPreviewActive(row: IliasCourseRow): boolean {
    return this.chatPreviewService.isChatActive$.value
      && this.activePreviewRefId === row.course.external_id;
  }

  /** Öffnet das Widget im Portal mit dem Kontext dieser Platzierung — also
   *  so, wie es später im ILIAS-Kurs antwortet. Nochmal geklickt schliesst es.
   *
   *  Anders als bei Moodle reist keine Kurs-Kennung des LMS mit: ILIAS nennt
   *  beim Launch die ref_id als `context_id`, und die Wissensbasis hängt am
   *  AlphaTutor-Kurs der Platzierung. Genau diese beiden gehen mit. */
  previewPlacement(row: IliasCourseRow, event?: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.isPreviewActive(row)) {
      this.chatPreviewService.closeChat();
      this.activePreviewRefId = null;
      return;
    }

    const botId = row.placement?.bot_id;
    if (!botId) {
      this.toastr.warning(
        this.t.instant('pages.tutors.placements.ilias.previewNoAgent'), '',
      );
      return;
    }

    const placementUuid = row.placement?.id || null;
    const courseId = row.placement?.course_id && row.placement.course_id !== this.clientId
      ? row.placement.course_id
      : null;
    this.chatPreviewService.connect(undefined, botId, {
      placementUuid,
      placement_uuid: placementUuid,
      courseId,
      context: {
        courseId,
        iliasRefId: row.course.external_id,
        iliasCourseTitle: row.course.title,
      },
    });
    this.activePreviewRefId = row.course.external_id;
  }

  /** Der AlphaTutor-Kurs hinter der Platzierung. Beide Inhaltsansichten
   *  hängen daran, und der Inhaltsabgleich auch: dort liegen Abschnitte und
   *  Dateien. Der Platzhalter „kein Kurs" (client_id) zählt nicht — ihn
   *  durchzureichen erzeugt einen Fremdschlüsselfehler. */
  alphaCourseId(row: IliasCourseRow): string | null {
    const id = row.placement?.course_id;
    return id && id !== this.clientId ? id : null;
  }

  /** Was der Inhaltsabgleich in die Wissensbasis gelegt hat. Derselbe Dialog
   *  wie bei Moodle — er nimmt ohnehin unsere Kurskennung, und die trägt die
   *  ILIAS-Platzierung direkt. */
  openSyncedContent(row: IliasCourseRow): void {
    const courseId = this.alphaCourseId(row);
    if (!courseId) return;
    this.dialogService.open(CourseStructureDialogComponent, {
      context: { courseId, courseName: row.course.title },
    });
  }

  /** Die gegliederte Kursdidaktik. Derselbe Dialog wie im Moodle-Dashboard,
   *  nur mit `byCourseId` — dort kommt die kanonische Moodle-Kennung an, hier
   *  unsere eigene. */
  openCourseStructure(row: IliasCourseRow): void {
    const courseId = this.alphaCourseId(row);
    if (!courseId) return;
    this.dialogService.open(ParsedCourseStructureDialogComponent, {
      context: {
        course: { id: courseId, fullname: row.course.title },
        byCourseId: true,
      },
      hasBackdrop: true,
    });
  }

  // ── Zeilenmenü ────────────────────────────────────────────────────
  /** Das Menü läuft über Nebulars Kontextmenü, nicht über einen eigenen
   *  Aufklapper in der Zelle.
   *
   *  Grund ist der Scroll-Rahmen der Tabelle: `overflow-x: auto` beschneidet
   *  absolut positionierte Kinder in **beiden** Achsen, ein Aufklapper in der
   *  Aktionszelle wird also an der Tabellenkante abgeschnitten. Nebular hängt
   *  sein Menü in den CDK-Overlay-Container ausserhalb der Tabelle — dieselbe
   *  Mechanik, aus der Auswahlfelder und Tooltips hier unbehelligt bleiben. */
  private readonly menuTagPrefix = 'iliasRowMenu-';

  rowMenuTag(row: IliasCourseRow): string {
    return `${this.menuTagPrefix}${row.course.external_id}`;
  }

  /** Zwischengespeicherte Einträge je Zeile.
   *
   *  Zwei Gründe. Erstens wirft `NbContextMenuDirective.validateItems` bei
   *  einer leeren Liste — eine Zeile ohne Platzierung hat aber keine Einträge,
   *  und genau daran ist die Seite gestorben. Zweitens würde ein bei jedem
   *  Änderungslauf neu gebautes Array die Direktive jedes Mal neu aufsetzen;
   *  die Kennung muss stabil bleiben, solange sich an der Zeile nichts ändert. */
  private menuCache = new Map<number, { sig: string; items: NbMenuItem[] }>();

  private rowSignature(row: IliasCourseRow): string {
    const p = row.placement;
    return [
      p?.id || '', p?.bot_id || '', p?.course_id || '',
      p?.course_sync_enabled ? '1' : '0',
      this.canPlaceInIlias ? '1' : '0',
    ].join('|');
  }

  /** Die Einträge einer Zeile. Dieselben Fälle wie bei Moodle, soweit es sie
   *  in ILIAS gibt — „In ILIAS anlegen" kommt hinzu, weil der Moodle-Weg das
   *  nicht kann. Leere Liste heisst: kein Menü anzeigen (siehe Vorlage). */
  rowMenuItems(row: IliasCourseRow): NbMenuItem[] {
    const sig = this.rowSignature(row);
    const cached = this.menuCache.get(row.course.external_id);
    if (cached && cached.sig === sig) return cached.items;

    const t = (key: string) =>
      this.t.instant(`pages.tutors.placements.ilias.rowActions.${key}`);
    const items: NbMenuItem[] = [];
    if (row.placement?.bot_id) {
      items.push({ title: t('agentSettings'), icon: { icon: 'aiMagic', pack: 'al' } as any,
                   data: { action: 'agentSettings' } });
    }
    if (row.placement) {
      items.push({ title: t('openPlacement'), icon: 'edit-outline',
                   data: { action: 'openPlacement' } });
    }
    if (this.canPlaceInIlias && row.placement) {
      items.push({ title: t('placeInIlias'), icon: 'plus-square-outline',
                   data: { action: 'placeInIlias' } });
    }
    if (row.placement?.course_sync_enabled && this.alphaCourseId(row)) {
      items.push({ title: t('syncedContent'), icon: 'list-outline',
                   data: { action: 'syncedContent' } });
      items.push({ title: t('viewStructure'), icon: 'book-open-outline',
                   data: { action: 'viewStructure' } });
    }
    if (row.placement?.course_sync_enabled) {
      items.push({ title: t('refreshContent'), icon: 'refresh-outline',
                   data: { action: 'refreshContent' } });
    }
    if (row.placement) {
      items.push({ title: t('removePlacement'), icon: 'trash-2-outline',
                   data: { action: 'removePlacement' } });
    }
    this.menuCache.set(row.course.external_id, { sig, items });
    return items;
  }

  /** Ein Abonnement für alle Zeilen: die Marke trägt die ref_id, daraus
   *  findet sich die Zeile wieder. */
  private listenToRowMenu(): void {
    this.nbMenuService
      .onItemClick()
      .pipe(
        filter(({ tag }) => !!tag && tag.startsWith(this.menuTagPrefix)),
        takeUntil(this.destroy$),
      )
      .subscribe(({ tag, item }: any) => {
        const refId = Number(String(tag).slice(this.menuTagPrefix.length));
        const row = this.rows.find((r) => r.course.external_id === refId);
        const action = item?.data?.action;
        if (!row || !action) return;
        this.runRowAction(row, action);
      });
  }

  private runRowAction(row: IliasCourseRow, key: string): void {
    switch (key) {
      case 'agentSettings':
        if (row.placement?.bot_id) {
          this.router.navigate(['/tutors', row.placement.bot_id, 'ai-settings']);
        }
        break;
      case 'openPlacement':
        if (row.placement) this.openEdit(row.placement);
        break;
      case 'placeInIlias':
        if (row.placement) this.placeInIlias(row.placement);
        break;
      case 'syncedContent':
        this.openSyncedContent(row);
        break;
      case 'viewStructure':
        this.openCourseStructure(row);
        break;
      case 'refreshContent':
        // Erneut einschalten heisst erneut holen: der Endpunkt ist absichtlich
        // wiederholbar, damit genau das geht.
        this.refreshContent(row);
        break;
      case 'removePlacement':
        if (row.placement) this.remove(row.placement);
        break;
    }
  }

  // ── Sammelaktionen ────────────────────────────────────────────────
  bulkAgentId: string | null = null;
  bulkBusy = false;

  /** Einen Agenten auf alle gewählten Zeilen setzen. Nacheinander statt
   *  parallel: der Server legt Platzierungen an, und eine Welle gleichzeitiger
   *  Anlagen auf demselben Kurs wäre eine vermeidbare Wettlaufsituation. */
  applyBulkAgent(): void {
    const rows = this.selectedRows;
    if (!this.bulkAgentId || rows.length === 0 || this.bulkBusy) return;
    this.bulkBusy = true;
    const botId = this.bulkAgentId;
    const run = (index: number) => {
      if (index >= rows.length) {
        this.bulkBusy = false;
        this.selected.clear();
        this.toastr.success(
          this.t.instant('pages.tutors.placements.ilias.bulk.done', { count: rows.length }), '',
        );
        this.reload();
        return;
      }
      const row = rows[index];
      const call = row.placement
        ? this.lms.updatePlacement(row.placement.id, { bot_id: botId })
        : this.lms.createPlacement({
            registration_id: this.registration.id,
            lti_context_id: String(row.course.external_id),
            bot_id: botId,
          });
      call.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => run(index + 1),
        error: () => run(index + 1),
      });
    };
    run(0);
  }

  /** Inhaltsabgleich für alle gewählten Zeilen mit Agent. Zeilen ohne Agent
   *  werden übersprungen statt abgelehnt — sonst bliebe eine gemischte
   *  Auswahl ganz ohne Wirkung. */
  applyBulkContentSync(on: boolean): void {
    // Beim Einschalten zaehlt jede gewaehlte Zeile: die Platzierung entsteht
    // bei Bedarf, genau wie beim einzelnen Schalter. Vorher fielen Zeilen ohne
    // Platzierung stillschweigend heraus — die Sammelaktion tat fuer sie
    // nichts und sagte es auch nicht.
    const rows = on ? this.selectedRows : this.selectedRows.filter((r) => !!r.placement);
    if (rows.length === 0 || this.bulkBusy) return;
    this.bulkBusy = true;
    const run = (index: number) => {
      if (index >= rows.length) {
        this.bulkBusy = false;
        this.selected.clear();
        this.reload();
        return;
      }
      const row = rows[index];
      const call: Observable<any> = on
        ? this.ensurePlacement(row).pipe(switchMap((placement) => {
            row.placement = placement;
            return this.lms.enableIliasCourseSync(placement.id);
          }))
        : this.lms.disableIliasCourseSync(row.placement!.id);
      call.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => run(index + 1),
        error: () => run(index + 1),
      });
    };
    run(0);
  }

  tutorName(botId: string | null | undefined): string {
    if (!botId) return '';
    const tutor = this.tutors.find((t) => t.id === botId);
    return tutor ? tutor.name : botId;
  }

  /** Der Anzeigename, falls er sich vom Namen unterscheidet. Getrennt gehalten,
   *  weil die Auswahlbeschriftung ihn wie bei Moodle als eigene, gedämpfte
   *  Zeile führt statt mit einem Mittelpunkt anzuhängen. */
  tutorVisual(botId: string | null | undefined): string | null {
    if (!botId) return null;
    const tutor = this.tutors.find((t) => t.id === botId);
    const visual = (tutor?.visual_name || '').trim();
    return visual && visual !== tutor?.name ? visual : null;
  }

  courseName(courseId: string | null | undefined): string {
    if (!courseId || (this.clientId && courseId === this.clientId)) return '';
    const course = this.courses.find((c) => c.id === courseId);
    return course ? course.course_name : courseId;
  }

  /** Lebt die Anbindung? Dieselbe Frage wie die Zustandsmarke der
   *  Moodle-Karte beantwortet, nur mit den Feldern, die eine LTI-Registrierung
   *  hat: Zugangsmaterial vorhanden und nicht pausiert. */
  isConnected(): boolean {
    const reg: any = this.registration;
    if (reg?.disabled_at) return false;
    const status = (reg?.connection_status || '').toLowerCase();
    if (status) return status === 'connected' || status === 'ready';
    return !!reg?.lti_client_id && reg.lti_client_id !== 'pending';
  }

  issuerHost(): string {
    const raw = this.registration.lti_issuer || '';
    try {
      return new URL(raw).hostname || raw;
    } catch {
      return raw.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    }
  }

  /** ILIAS-Version aus der Plattformkonfiguration. Steuert die Hinweise:
   *  Deep Linking und AGS gibt es erst ab 11. */
  get iliasVersion(): string | null {
    return (this.registration.lti_platform_version || '').trim() || null;
  }

  get majorVersion(): number {
    const m = /^\s*(\d+)/.exec(this.iliasVersion || '');
    return m ? Number(m[1]) : 0;
  }

  /** Vor ILIAS 11 kann die Lehrkraft den Agent nicht im LMS auswählen —
   *  dort bleibt nur das manuelle Anlegen mit der ref_id. */
  get supportsDeepLinking(): boolean {
    return this.majorVersion >= 11;
  }

  /** Wo der Agent dieser Zeile auftaucht.
   *
   *  Die Zeile ist dieselbe, das Ergebnis nicht:
   *
   *    Plugin, kursweit  -> auf jeder Seite des Kurses, ohne dass in ILIAS
   *                         etwas angelegt werden muss
   *    LTI, kursweit     -> in jedem „Externes Tool"-Objekt dieses Kurses,
   *                         das keinen eigenen Agenten traegt
   *    LTI, Aktivitaet   -> nur in genau diesem Objekt
   *
   *  Beides zugleich ist der Normalfall bei einer gewachsenen Verbindung:
   *  eine ILIAS-Zeile fuehrt oft LTI UND Plugin. Dann gilt beides, und das
   *  muss dastehen — sonst haelt man die Zuordnung fuer das eine, waehrend
   *  sie auch das andere tut.
   */
  placementEffectKey(row: IliasCourseRow): string {
    const base = 'pages.tutors.placements.ilias.effect';
    // `row.placement` ist per Definition die kursweite Zeile
    // (`placementFor` filtert Aktivitaeten heraus) — ein Zweig auf
    // `lti_resource_link_id` waere hier nie erreichbar. Die Aktivitaeten
    // stehen separat in `activityPlacements`.
    const plugin = LmsRegistryService.pluginPlacesAgents(this.registration);
    const lti = LmsRegistryService.canLaunchLti(this.registration);
    if (plugin && lti) return `${base}.both`;
    if (plugin) return `${base}.plugin`;
    return `${base}.lti`;
  }

  /** Rendert das Plugin dieser Verbindung Platzierungen? Einmal ausgewertet,
   *  damit das Template es nicht bei jedem Durchlauf neu herleitet. */
  get pluginPlaces(): boolean {
    return LmsRegistryService.pluginPlacesAgents(this.registration);
  }

  /** Der Kurs hat einen Agenten, aber nur im LTI-Objekt.
   *
   *  Die Lage nach „Plugin nachtraeglich dazugekoppelt": die Lehrkraft hat
   *  den Agenten vor Monaten per Deep Linking in eine Aktivitaet gesetzt,
   *  das Plugin liest solche Zeilen nicht, und auf den Kursseiten erscheint
   *  nichts. In dieser Karte sah der Kurs bisher schlicht unbesetzt aus. */
  hasOnlyActivityPlacements(row: IliasCourseRow): boolean {
    return !this.rowHasAgentPlacement(row) && row.activityPlacements.length > 0;
  }

  /** Aktivitaeten mit eigenem Agenten NEBEN einer kursweiten Zuordnung.
   *  Sie gehen der kursweiten vor — das gehoert dazugesagt. */
  hasExtraActivityPlacements(row: IliasCourseRow): boolean {
    return this.rowHasAgentPlacement(row) && row.activityPlacements.length > 0;
  }

  openCreate(): void {
    this.openDialog(null);
  }

  /** Platzieren aus der Zeile: derselbe Dialog, nur mit vorbelegtem Kurs.
   *  Die ref_id steht in der Zeile, niemand soll sie abtippen. */
  openCreateFor(row: IliasCourseRow): void {
    // Trägt die Zeile schon eine reine Sync-Platzierung, bekommt sie ihren
    // Agenten dort — eine zweite Platzierung auf denselben Kurs wäre ein
    // Doppelgänger, den danach niemand mehr auseinanderhält.
    if (row.placement) {
      this.openEdit(row.placement);
      return;
    }
    this.openDialog(null, String(row.course.external_id));
  }

  /** Alles neu laden — Platzierungen, Kurse, Zugang. Entspricht dem
   *  Aktualisieren-Knopf der Moodle-Karte. */
  /** „Aktualisieren" holt bei ILIAS nach, nicht nur aus unserer Datenbank.
   *
   *  Vorher las der Knopf ausschliesslich neu, was wir ohnehin schon hatten —
   *  ein frisch in ILIAS angelegter Kurs tauchte dadurch **nie** auf, egal wie
   *  oft man klickte. Die Methode fuer den echten Abgleich gab es bereits, sie
   *  wurde nur nirgends aufgerufen. Die ILIAS-Uebersicht macht es seit jeher
   *  richtig (`runSync`); hier ist es jetzt derselbe Aufruf.
   *
   *  Der Abgleich darf scheitern, ohne den Knopf nutzlos zu machen: danach
   *  wird in jedem Fall neu geladen. */
  reloadAll(): void {
    this.menuCache.clear();
    if (this.syncing) return;
    this.syncing = true;
    this.lms.runIliasSync(this.registration.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          this.syncing = false;
          this.toastr.success(
            this.t.instant('pages.tutors.placements.ilias.syncedToast', {
              courses: result?.tree?.courses ?? 0,
              created: result?.tree?.created ?? 0,
            }), '',
          );
          this.reload();
          this.loadCourses();
          this.loadSoap();
        },
        error: (err) => {
          this.syncing = false;
          this.toastr.danger(this.resolveError(err), '');
          this.reload();
          this.loadCourses();
          this.loadSoap();
        },
      });
  }

  openEdit(placement: LmsPlacement): void {
    this.openDialog(placement);
  }

  private openDialog(placement: LmsPlacement | null, presetContextId: string | null = null): void {
    this.dialogService
      .open(LtiPlacementDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: false,
        context: {
          registration: this.registration,
          placement,
          tutors: this.tutors,
          courses: this.courses,
          clientId: this.clientId,
          presetContextId,
        },
      })
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe((saved: boolean) => {
        if (saved) this.reload();
      });
  }

  remove(placement: LmsPlacement): void {
    this.dialogService
      .open(ConfirmDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: true,
        context: {
          title: 'pages.tutors.placements.lti.deleteDialog.title',
          message: 'pages.tutors.placements.lti.deleteDialog.message',
        },
      })
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.lms.deletePlacement(placement.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.toastr.success(this.t.instant('pages.tutors.placements.lti.deletedToast'), '');
            this.reload();
          },
          error: (err) => this.toastr.danger(this.resolveError(err), ''),
        });
      });
  }

  // ── SOAP ──────────────────────────────────────────────────────────
  private loadSoap(): void {
    this.lms
      .getIliasSoap(this.registration.id)
      .pipe(takeUntil(this.destroy$), catchError(() => of(null)))
      .subscribe((cfg) => {
        this.soap = cfg;
        if (!cfg) return;
        this.form = {
          // Ohne gespeicherten Endpoint der Vorschlag aus der Issuer-URL —
          // der Pfad wechselte mit ILIAS 10, das rät niemand gern selbst.
          soap_url: cfg.soap_url || cfg.suggested_soap_url || '',
          ilias_client: cfg.ilias_client || '',
          username: cfg.username || '',
          password: '',
          template_ref_id: cfg.template_ref_id ?? null,
        };
        // Unkonfiguriert gleich aufklappen: ohne SOAP fehlt der halbe Nutzen.
        this.soapOpen = !cfg.configured;
      });
  }

  get soapStatus(): 'ok' | 'error' | 'incomplete' {
    if (!this.soap?.configured) return 'incomplete';
    if (this.soap.last_error) return 'error';
    return this.soap.last_ok_at ? 'ok' : 'incomplete';
  }

  toggleSoap(): void {
    this.soapOpen = !this.soapOpen;
  }

  saveSoap(): void {
    if (this.soapSaving) return;
    this.soapSaving = true;
    const payload: IliasSoapInput = {
      soap_url: this.form.soap_url.trim(),
      ilias_client: this.form.ilias_client.trim(),
      username: this.form.username.trim(),
      template_ref_id: this.form.template_ref_id,
    };
    // Leeres Feld = Passwort unverändert lassen. Mitsenden würde es löschen.
    if (this.form.password) payload.password = this.form.password;

    this.lms
      .saveIliasSoap(this.registration.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cfg) => {
          this.soapSaving = false;
          this.soap = cfg;
          this.form.password = '';
          this.toastr.success(this.t.instant('pages.tutors.placements.ilias.soapSavedToast'), '');
        },
        error: (err) => {
          this.soapSaving = false;
          this.toastr.danger(this.resolveError(err), '');
        },
      });
  }

  testSoap(): void {
    if (this.soapTesting) return;
    this.soapTesting = true;
    this.lms
      .testIliasSoap(this.registration.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.soapTesting = false;
          if (result.ok) {
            this.toastr.success(
              this.t.instant('pages.tutors.placements.ilias.soapTestOk', {
                count: result.root_children ?? 0,
              }),
              '',
            );
          } else {
            // Der Schritt sagt mehr als der Text: `login` = Zugangsdaten,
            // `read` = Zugangsdaten stimmen, aber die Rolle darf nichts lesen.
            this.toastr.danger(
              `${this.t.instant('pages.tutors.placements.ilias.soapTestStep.' + result.step)}: ${result.detail || ''}`,
              '',
            );
          }
          this.loadSoap();
        },
        error: (err) => {
          this.soapTesting = false;
          this.toastr.danger(this.resolveError(err), '');
        },
      });
  }

  syncCourses(): void {
    if (this.syncing) return;
    this.syncing = true;
    this.lms
      .syncIliasCourses(this.registration.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.syncing = false;
          this.toastr.success(
            this.t.instant('pages.tutors.placements.ilias.syncedToast', {
              courses: result.courses,
              created: result.created,
            }),
            '',
          );
          this.loadSoap();
        },
        error: (err) => {
          this.syncing = false;
          this.toastr.danger(this.resolveError(err), '');
        },
      });
  }

  private resolveError(err: any): string {
    const detail = err?.error?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object') {
      return detail.message || detail.code || this.t.instant('toasts.error');
    }
    return err?.message || this.t.instant('toasts.error');
  }
}
